import type {
  Hook,
  OnOtpCodeChange,
  OnOtpCodeReset,
  OnOtpDataReset,
  OtpId,
  OtpSessionToken,
} from './types';
import type { OtpCode } from '@sgcmpteltd/uikit-react/components/otp-input';

import { useEffect, useState } from 'react';

import { isOtpCode } from '@sgcmpteltd/uikit-react/components/otp-input';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { useCountDown } from '../use-count-down';

dayjs.extend(utc);

export const useOtp: Hook = ({ onOtpAttemptsReached = () => {}, onOtpVerify }) => {
  const [otpId, onOtpIdChange] = useState<OtpId | null>(null);
  const [otpCodeExpiredTime, onOtpCodeExpiredTimeChange] = useState<
    number | null
  >(null);
  const [isOtpCodeInvalid, onIsOtpCodeInvalidChange] = useState<boolean | null>(
    null,
  );
  const [otpSessionToken, onOtpSessionTokenChange]
    = useState<OtpSessionToken | null>(null);
  const [nextOtpResendableTime, onNextOtpResendableTimeChange] = useState<
    number | null
  >(null);
  const [otpCodeRetryAttempts, onOtpCodeRetryAttemptsChange] = useState<
    number | null
  >(null);
  const [otpRetryAttempts, onOtpRetryAttemptsChange] = useState<number | null>(
    null,
  );
  const [isOtpCodeExpired, setIsOtpCodeExpired] = useState(false);
  const [otpCode, setOtpCode] = useState<OtpCode>([
    undefined,
    undefined,
    undefined,
    undefined,
  ]);
  const [isOtpVerifyPending, setIsOtpVerifyPending] = useState(false);
  const [nextOtpResendableTimestamp, setNextOtpResendableTimestamp]
    = useState(0);

  const onOtpCodeReset: OnOtpCodeReset = () => {
    setOtpCode([undefined, undefined, undefined, undefined]);
  };

  const onOtpDataReset: OnOtpDataReset = () => {
    onOtpIdChange(null);
    onOtpCodeExpiredTimeChange(null);
    onIsOtpCodeInvalidChange(null);
    onOtpSessionTokenChange(null);
    onNextOtpResendableTimeChange(null);
    onOtpCodeRetryAttemptsChange(null);
    onOtpRetryAttemptsChange(null);
    setIsOtpVerifyPending(false);
    setIsOtpCodeExpired(false);
    setNextOtpResendableTimestamp(0);
    onOtpCodeReset();
  };

  const onOtpCodeChange: OnOtpCodeChange = async (newOtpCode) => {
    setOtpCode(newOtpCode);

    if (!isOtpCode(newOtpCode)) {
      return;
    }

    setIsOtpVerifyPending(true);

    try {
      await onOtpVerify(newOtpCode);
    } finally {
      setIsOtpVerifyPending(false);
    }
  };

  const onChangeIsOtpVerifyPending = (bool: boolean) => {
    setIsOtpVerifyPending(bool);
  };

  const { remainSeconds: nextOtpResendableRemainSeconds } = useCountDown({
    targetTimestamp: nextOtpResendableTimestamp,
  });

  useEffect(() => {
    if (!otpCodeExpiredTime) {
      return;
    }

    const otpCodeExpiredDate = dayjs.unix(otpCodeExpiredTime);

    if (!otpCodeExpiredDate.isValid()) {
      return undefined;
    }

    const differncesMsec = otpCodeExpiredDate.diff(dayjs().utc());

    if (differncesMsec <= 0) {
      return undefined;
    }

    const { clearTimeout, setTimeout } = window;
    const optCodeExpiredTimer = setTimeout(() => {
      setIsOtpCodeExpired(true);
    }, differncesMsec);

    return () => {
      clearTimeout(optCodeExpiredTimer);
    };
  }, [otpCodeExpiredTime]);

  const hasOtpRetryAttempts
    = typeof otpRetryAttempts === 'number' && otpRetryAttempts > 0;

  const hasOtpCodeRetryAttempts
    = typeof otpCodeRetryAttempts === 'number' && otpCodeRetryAttempts > 0;

  useEffect(() => {
    if (otpRetryAttempts === null || hasOtpRetryAttempts) {
      return;
    }

    onOtpAttemptsReached();
  }, [otpRetryAttempts, hasOtpRetryAttempts, onOtpAttemptsReached]);

  useEffect(() => {
    if (!nextOtpResendableTime) {
      return;
    }

    const nextOtpResendableDate = dayjs.unix(nextOtpResendableTime);

    if (!nextOtpResendableDate.isValid()) {
      return;
    }

    setNextOtpResendableTimestamp(
      hasOtpRetryAttempts ? nextOtpResendableTime : 0,
    );
  }, [
    nextOtpResendableRemainSeconds,
    hasOtpRetryAttempts,
    nextOtpResendableTime,
    otpRetryAttempts,
    onOtpAttemptsReached,
  ]);

  return {
    hasOtpCodeRetryAttempts,
    hasOtpRetryAttempts,
    isOtpCodeExpired,
    isOtpCodeInvalid,
    isOtpVerifyPending,
    nextOtpResendableRemainSeconds,
    nextOtpResendableTime,
    onChangeIsOtpVerifyPending,
    onIsOtpCodeInvalidChange,
    onNextOtpResendableTimeChange,
    onOtpCodeChange,
    onOtpCodeExpiredTimeChange,
    onOtpCodeReset,
    onOtpCodeRetryAttemptsChange,
    onOtpDataReset,
    onOtpIdChange,
    onOtpRetryAttemptsChange,
    onOtpSessionTokenChange,
    otpCode,
    otpCodeExpiredTime,
    otpCodeRetryAttempts,
    otpId,
    otpRetryAttempts,
    otpSessionToken,
    setIsOtpCodeExpired,
  };
};
